import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useScale — Web Serial API integration for USB/RS-232 weighing scales.
 *
 * Most industrial scales emit continuous ASCII frames like:
 *   "ST,GS,+003.250 kg\r\n"
 *   "+003.250 kg\r\n"
 *   "  3.250kg"
 * We extract the first floating-point number we see, in kg.
 *
 * A reading is considered "stable" when the same value is seen N times in a row.
 *
 * Notes:
 * - Web Serial requires a secure context (HTTPS) and a user gesture to call requestPort.
 * - Supported in Chromium-based browsers (Chrome, Edge, Brave). Not in Firefox/Safari.
 */
export function isWebSerialSupported() {
  return typeof navigator !== "undefined" && "serial" in navigator;
}

const NUM_RE = /([-+]?\d+(?:\.\d+)?)/;

export function useScale({ baudRate = 9600, stableSamples = 3 } = {}) {
  const [supported] = useState(isWebSerialSupported());
  const [connected, setConnected] = useState(false);
  const [reading, setReading] = useState(null);      // last numeric value (kg)
  const [stable, setStable] = useState(false);
  const [error, setError] = useState("");
  const portRef = useRef(null);
  const readerRef = useRef(null);
  const lastRef = useRef({ value: null, count: 0 });
  const bufferRef = useRef("");
  const onStableRef = useRef(null);

  const setOnStable = useCallback((fn) => {
    onStableRef.current = fn;
  }, []);

  const parseFrame = useCallback((text) => {
    const match = text.match(NUM_RE);
    if (!match) return;
    const v = parseFloat(match[1]);
    if (!Number.isFinite(v)) return;
    setReading(v);

    // Stability detection
    const last = lastRef.current;
    if (last.value !== null && Math.abs(last.value - v) < 0.0005) {
      last.count += 1;
    } else {
      last.value = v;
      last.count = 1;
    }
    if (last.count >= stableSamples) {
      setStable(true);
      onStableRef.current?.(v);
    } else {
      setStable(false);
    }
  }, [stableSamples]);

  const readLoop = useCallback(async () => {
    const port = portRef.current;
    if (!port) return;
    const decoder = new TextDecoderStream();
    const inputDone = port.readable.pipeTo(decoder.writable).catch(() => {});
    const inputStream = decoder.readable;
    const reader = inputStream.getReader();
    readerRef.current = reader;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        bufferRef.current += value;
        // Split on common line endings; process every newline-terminated frame
        const parts = bufferRef.current.split(/[\r\n]+/);
        bufferRef.current = parts.pop() || "";
        for (const p of parts) {
          if (p.trim()) parseFrame(p);
        }
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      try { reader.releaseLock(); } catch {}
      await inputDone;
    }
  }, [parseFrame]);

  const connect = useCallback(async () => {
    setError("");
    if (!supported) {
      setError("Web Serial not supported in this browser. Use Chrome / Edge on desktop.");
      return false;
    }
    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate, dataBits: 8, stopBits: 1, parity: "none", flowControl: "none" });
      portRef.current = port;
      setConnected(true);
      readLoop();
      return true;
    } catch (e) {
      setError(String(e.message || e));
      return false;
    }
  }, [baudRate, readLoop, supported]);

  const disconnect = useCallback(async () => {
    try {
      if (readerRef.current) {
        try { await readerRef.current.cancel(); } catch {}
        readerRef.current = null;
      }
      if (portRef.current) {
        try { await portRef.current.close(); } catch {}
        portRef.current = null;
      }
    } finally {
      setConnected(false);
      setStable(false);
      setReading(null);
      lastRef.current = { value: null, count: 0 };
      bufferRef.current = "";
    }
  }, []);

  useEffect(() => {
    return () => { disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { supported, connected, reading, stable, error, connect, disconnect, setOnStable };
}
